"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@clerk/nextjs";
import {
  ArrowRight,
  BookOpen,
  Code2,
  FileText,
  GraduationCap,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getLastStudiedPages,
  type LastStudiedPage,
} from "@/lib/server-actions/lastStudiedActions";
import {
  STUDY_CATEGORY_INDEX,
  STUDY_CATEGORY_LABEL,
  STUDY_CATEGORY_ORDER,
  getStudyPageTitle,
  type StudyCategory,
} from "@/lib/route-layout";

interface ContinueStudyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** 카테고리별 아이콘 + 강조색(라이트/다크). 홈 사이드바 카드와 동일한 파스텔 톤. */
const CATEGORY_META: Record<
  StudyCategory,
  { icon: LucideIcon; iconClass: string }
> = {
  theory: {
    icon: BookOpen,
    iconClass:
      "bg-sky-100 text-sky-600 dark:bg-sky-950/60 dark:text-sky-300",
  },
  coding: {
    icon: Code2,
    iconClass:
      "bg-violet-100 text-violet-600 dark:bg-violet-950/60 dark:text-violet-300",
  },
  "past-exam": {
    icon: FileText,
    iconClass:
      "bg-orange-100 text-orange-600 dark:bg-orange-950/60 dark:text-orange-300",
  },
  "mock-exam": {
    icon: GraduationCap,
    iconClass:
      "bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-300",
  },
};

/**
 * 홈 "공부 시작하기" 이어학습 모달 (JCG-2166).
 *
 * 로그인 사용자가 CTA 를 누르면 열리며, 카테고리(이론·코딩·기출·모의고사)별 가장 최근 방문
 * 학습 페이지를 선택지로 보여준다. 각 행을 누르면 그 페이지로 이동하고, 이력이 없는 카테고리는
 * 카테고리 인덱스(/theory 등)로 폴백한다. shadcn Dialog 패턴은 PreviousSessionDialog 참고.
 */
export default function ContinueStudyDialog({
  open,
  onOpenChange,
}: ContinueStudyDialogProps) {
  const router = useRouter();
  // 비로그인(게스트)이 "회원가입없이 보기"로 연 경우엔 이력이 항상 비어 있으므로,
  // 빈 카테고리 문구를 "처음부터" 대신 회원가입 유도로 바꾼다(로그인 사용자는 기존 문구 유지).
  const { isSignedIn } = useAuth();
  const [pages, setPages] = useState<LastStudiedPage[] | null>(null);

  // 열릴 때마다 최신 이력 재조회(다른 탭/디바이스 방문 반영).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const data = await getLastStudiedPages();
      if (cancelled) return;
      setPages(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const byCategory = new Map<StudyCategory, LastStudiedPage>();
  for (const page of pages ?? []) byCategory.set(page.category, page);

  const handleGo = (category: StudyCategory) => {
    const record = byCategory.get(category);
    const target = record?.pathname || STUDY_CATEGORY_INDEX[category];
    onOpenChange(false);
    router.push(target);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-md overscroll-contain p-4 sm:max-w-md sm:p-6">
        <DialogHeader className="min-w-0 pr-8 text-left">
          <DialogTitle>이어서 공부하기</DialogTitle>
          <DialogDescription>
            최근에 보던 학습 페이지로 바로 이동해요.
          </DialogDescription>
        </DialogHeader>

        {pages === null ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="min-w-0 space-y-2">
            {STUDY_CATEGORY_ORDER.map((category) => {
              const record = byCategory.get(category);
              const meta = CATEGORY_META[category];
              const Icon = meta.icon;
              const hasHistory = !!record;
              const title = hasHistory
                ? record.title || getStudyPageTitle(record.pathname)
                : null;

              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => handleGo(category)}
                  className="group flex w-full min-w-0 items-center gap-3 overflow-hidden rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                >
                  <span
                    className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${meta.iconClass}`}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-foreground">
                      {STUDY_CATEGORY_LABEL[category]}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                      {hasHistory
                        ? title
                        : isSignedIn
                          ? "아직 본 페이지가 없어요 · 처음부터"
                          : "회원가입하면 최근 공부한 페이지가 보여요"}
                    </span>
                  </span>
                  <ArrowRight
                    className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
